'use client';

import { ChevronRightIcon, ChevronLeftIcon } from 'tdesign-icons-react';
import { animated, useSpring } from '@react-spring/web';
import { useEffect, useRef, useState } from 'react';
import { useMedia } from '@ssv-website/hooks';
import Link from 'next/link';
import { useMount } from 'react-use';
import { useRouter } from 'next/navigation';
import SSVLink from '../ssv-link';

interface IBreadcrumbsProps {
  data: Array<{
    title: string;
    link: string | null;
    noBlank?: boolean;
  }>;
  cssBox?: string;
  cssMobile?: string;
}

function debounce(func: () => void, wait: number = 100): () => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(func, wait);
  };
}

const useStickyLink = () => {
  const [placeholderHeight, setPlaceholderHeight] = useState(0);
  const [isSticky, setIsSticky] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useMedia('mobile');

  useEffect(() => {
    const handleScroll = debounce(() => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        //  setPlaceholderHeight(rect.height);
        setIsSticky(window.scrollY > 40 && rect.top <= (isMobile ? 48 : 72));
      }
    }, 0);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  return {
    ref,
    placeholderHeight,
    isSticky,
  };
};

const DesktopLink = ({
  data,
}: {
  data: Array<{
    title: string;
    link: string | null;
    noBlank?: boolean;
  }>;
}) => {
  return data.map((m, i) => (
    <div key={i} className="flex items-center">
      {!m.link ? (
        <div className="flex justify-center cursor-pointer">
          <span className="mobile:text-primary/60 mobile:text-xs">
            {m.title}
          </span>
        </div>
      ) : (
        <SSVLink
          url={m.link || '/'}
          isBlank={!m.noBlank}
          className="flex justify-center"
        >
          <span className="mobile:text-primary/60 mobile:text-xs">
            {m.title}
          </span>
        </SSVLink>
      )}
      {i !== data.length - 1 && (
        <span className="mx-[10px]">
          <ChevronRightIcon className="h-4 w-4 text-primary/[.6]" />
        </span>
      )}
    </div>
  ));
};

const MobileLink = ({
  data,
}: {
  data: Array<{
    title: string;
    link: string | null;
  }>;
}) => {
  const router = useRouter();
  const item = data.length ? data[data.length - 1] : { title: '' };

  return (
    <div className="flex gap-5 items-center">
      <ChevronLeftIcon color="#353F49" size="large" onClick={router.back} />
      <div className="w-px h-[14px] bg-primary/[.21]" />
      <span className="mobile:text-primary/60 mobile:text-xs">
        {item.title}
      </span>
    </div>
  );
};

export const Breadcrumbs: React.FC<IBreadcrumbsProps> = ({
  data = [],
  cssBox,
  cssMobile,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(true);
  const isMobile = useMedia('mobile');

  useEffect(() => {
    if (isMobile) return;
    if (ref.current) {
      const pcNavbar = document.querySelector('#pc-navbar') as HTMLElement;
      const navbarY = pcNavbar?.getClientRects()?.[0]?.height;

      const io = new IntersectionObserver(
        (entries) => {
          setIsInView(entries[0].isIntersecting);
        },
        { rootMargin: `-${navbarY ?? 0 + 1}px 0px 0px 0px` }
      );
      io.observe(ref.current);
      return () => io.disconnect();
    }
  }, [isMobile]);

  return (
    <div>
      <div
        className={`text-primary/[.6] text-base relative h-[62px] pt-10 mobile:hidden ${
          cssBox ?? ''
        }`}
      >
        <div className="h-px w-full" ref={ref} />
        <div
          className="flex justify-center items-center w-full desktop:px-5 h-[22px] absolute left-0 top-10 z-40"
          style={isInView ? {} : fixedStyle}
        >
          <div className="max-w-5xl w-full flex  items-center">
            <DesktopLink data={data} />
          </div>
        </div>
      </div>
      <div className={`hidden mobile:block pt-20 ${cssMobile}`}></div>
    </div>
  );
};

const fixedStyle = {
  position: 'fixed',
  top: 72,
  backgroundColor: '#fff',
  height: '46px',
} as React.CSSProperties;

export const _Breadcrumbs: React.FC<IBreadcrumbsProps> = ({
  data = [],
  cssBox,
}) => {
  const { ref, isSticky } = useStickyLink();
  const [isMounted, setIsMounted] = useState(false);
  const isMobile = useMedia('mobile');
  const mobileTop = isSticky ? 40 : 'auto';
  const desktopTop = isSticky ? 72 : 'auto';

  useMount(() => {
    setIsMounted(true);
  });
  const desktopHeight = isSticky ? 46 : 22;
  const mobileHeight = isSticky ? 40 : 22;
  return (
    <animated.div
      ref={ref}
      className={`text-primary/[.6] text-base -leading-[1px] flex justify-center items-center w-full px-0 mobile:px-5 ${
        isSticky ? 'shadow-top-op' + cssBox : cssBox ?? 'mt-10'
      }`}
      style={{
        height: isMobile ? mobileHeight : desktopHeight,
        top: isMobile ? mobileTop : desktopTop,
        left: isSticky ? 0 : 'auto',
        position: isSticky ? 'fixed' : 'relative',
        zIndex: isSticky ? 10 : 'auto',
        backgroundColor: isSticky ? 'white' : 'transparent',
      }}
    >
      <div className="max-w-5xl w-full flex items-center desktop:px-5">
        {isMounted && ((isMobile && isSticky) || !isMobile) && (
          <DesktopLink data={data} />
        )}
      </div>
    </animated.div>
  );
};
